import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { Send, AlertTriangle, Loader2, MessageCircle, Crown, Star, Sparkles, Smile, Lock } from 'lucide-react';
import { cn } from '../lib/utils';
import { ListenerTierBadge } from './ListenerTier';
import LiveStreamEffects, { VIPHighlightBadge, isUserVIP, VIPChatMessage } from './LiveStreamEffects';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



// Tier color mapping
const TIER_COLORS = {
  supporter: '#3B82F6',
  super_fan: '#8B5CF6',
  vip_fan: '#F59E0B',
  legend: '#EF4444'
};

export function ChatPanel({ 
  streamId, 
  getAuthHeaders, 
  hostUserId, 
  topFans = [],
  isHost = false,
  currentUserId = null
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vipUsers, setVipUsers] = useState([]);
  const [chatPrivileges, setChatPrivileges] = useState(null);
  const [emojiData, setEmojiData] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Helper to check if user is a top fan
  const getTopFanRank = (userId) => {
    const fan = topFans.find(f => f.user_id === userId);
    return fan ? fan.rank : null;
  };

  useEffect(() => {
    fetchMessages();
    fetchVipUsers();
    fetchChatPrivileges();
    // Poll for new messages every 2 seconds
    pollRef.current = setInterval(() => {
      fetchMessages();
      fetchVipUsers();
    }, 2000);
    
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [streamId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChatPrivileges = async () => {
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const [privResponse, emojiResponse] = await Promise.all([
        fetch(`${API}/chat/privileges`, { headers }),
        fetch(`${API}/chat/emojis`, { headers })
      ]);
      
      if (privResponse.ok) {
        setChatPrivileges(await privResponse.json());
      }
      if (emojiResponse.ok) {
        setEmojiData(await emojiResponse.json());
      }
    } catch (error) {
      // Silently fail - will use defaults
    }
  };

  const fetchVipUsers = async () => {
    try {
      const response = await fetch(`${API}/live/streams/${streamId}/effects`);
      if (response.ok) {
        const data = await response.json();
        setVipUsers(data.vip_users || []);
      }
    } catch (error) {
      // Silently fail - VIP effects are not critical
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API}/live/streams/${streamId}/chat?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/live/streams/${streamId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ message: newMessage.trim() })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        setMessages(prev => [...prev, result.data]);
        setNewMessage('');
        
        if (result.data.moderation_status === 'filtered') {
          toast.warning('Your message was filtered for inappropriate content');
        }
      } else {
        toast.error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      {/* Live Stream Effects (Pinned Messages, Shoutout Requests) */}
      <div className="px-3 pt-3">
        <LiveStreamEffects
          streamId={streamId}
          isHost={isHost}
          currentUserId={currentUserId}
          getAuthHeaders={getAuthHeaders}
        />
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No messages yet. Be the first to chat!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const topFanRank = getTopFanRank(msg.user_id);
            const userIsVIP = isUserVIP(msg.user_id, vipUsers);
            // Use tier-based color if available, otherwise fall back to existing logic
            const tierColor = msg.username_color || TIER_COLORS[msg.listener_tier];
            
            return (
              <VIPChatMessage key={msg.message_id} isVIP={userIsVIP}>
                <div
                  className={cn(
                    "text-sm",
                    msg.is_host && "bg-primary/10 rounded p-1.5",
                    topFanRank === 1 && !msg.is_host && !userIsVIP && "bg-amber-500/10 rounded p-1.5"
                  )}
                >
                  <span 
                    className={cn(
                      "font-medium mr-2",
                      msg.is_host ? "text-primary" : 
                      userIsVIP ? "text-amber-300" :
                      !tierColor && (
                        topFanRank === 1 ? "text-amber-400" :
                        topFanRank === 2 ? "text-gray-300" :
                        topFanRank === 3 ? "text-amber-600" :
                        "text-secondary"
                      )
                    )}
                    style={tierColor && !msg.is_host && !userIsVIP ? { 
                      color: tierColor,
                      ...(msg.gradient_username && {
                        background: `linear-gradient(90deg, ${tierColor}, #F97316)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      })
                    } : undefined}
                  >
                    {userIsVIP && (
                      <Sparkles className="w-3 h-3 inline mr-1 text-amber-400" />
                    )}
                    {topFanRank && topFanRank <= 3 && !userIsVIP && (
                      <Crown className="w-3 h-3 inline mr-1" />
                    )}
                    {msg.user_name}
                    {msg.is_host && (
                      <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1 border-primary/50">
                        HOST
                      </Badge>
                    )}
                    {userIsVIP && (
                      <Badge className="ml-1 text-[10px] py-0 px-1 bg-gradient-to-r from-amber-500 to-yellow-400 text-black">
                        VIP
                      </Badge>
                    )}
                    {topFanRank === 1 && !msg.is_host && !userIsVIP && (
                      <Badge className="ml-1 text-[10px] py-0 px-1 bg-amber-500 text-black">
                        #1 FAN
                      </Badge>
                    )}
                    {msg.listener_tier && msg.listener_tier !== 'none' && !msg.is_host && (
                      <Badge 
                        className="ml-1 text-[10px] py-0 px-1 capitalize"
                        style={{ backgroundColor: `${tierColor}20`, color: tierColor, borderColor: `${tierColor}50` }}
                        variant="outline"
                      >
                        {msg.listener_tier.replace('_', ' ')}
                      </Badge>
                    )}
                  </span>
                  <span className={cn(
                    "text-foreground",
                    msg.moderation_status === 'filtered' && "text-muted-foreground italic"
                  )}>
                    {msg.message}
                  </span>
                </div>
              </VIPChatMessage>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={handleSendMessage} className="p-3 border-t border-border">
        <div className="flex gap-2">
          {/* Emoji Picker */}
          <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="flex-shrink-0"
                data-testid="emoji-picker-btn"
              >
                <Smile className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2" align="start">
              <div className="space-y-3">
                {emojiData?.available && Object.entries(emojiData.available).map(([category, emojis]) => (
                  <div key={category}>
                    <p className="text-xs font-medium text-muted-foreground mb-1 capitalize">{category}</p>
                    <div className="flex flex-wrap gap-1">
                      {emojis.map((emoji) => (
                        <button
                          key={emoji.code}
                          type="button"
                          className="w-8 h-8 flex items-center justify-center hover:bg-muted/50 rounded text-lg"
                          onClick={() => {
                            setNewMessage(prev => prev + emoji.code);
                            setShowEmojiPicker(false);
                          }}
                          title={emoji.name}
                        >
                          {emoji.emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Locked emojis preview */}
                {emojiData?.locked && Object.keys(emojiData.locked).length > 0 && (
                  <div className="pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Unlock more with higher tiers
                    </p>
                    <div className="flex flex-wrap gap-1 opacity-50">
                      {Object.values(emojiData.locked).slice(0, 1).map(cat => 
                        cat.emojis?.slice(0, 4).map((emoji) => (
                          <span key={emoji.code} className="w-8 h-8 flex items-center justify-center text-lg">
                            {emoji.emoji}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                )}
                
                {!chatPrivileges?.can_use_custom_emojis && (
                  <p className="text-xs text-amber-400 text-center py-2">
                    Support artists to unlock custom emojis!
                  </p>
                )}
              </div>
            </PopoverContent>
          </Popover>
          
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Send a message..."
            maxLength={chatPrivileges?.max_message_length || 200}
            disabled={sending}
            className="flex-1 bg-muted/30"
            data-testid="chat-input"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!newMessage.trim() || sending}
            data-testid="send-chat-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <div className="flex justify-between items-center mt-1">
          <p className="text-xs text-muted-foreground">
            Be respectful. Violations may result in chat ban.
          </p>
          <p className="text-xs text-muted-foreground">
            {newMessage.length}/{chatPrivileges?.max_message_length || 200}
          </p>
        </div>
      </form>
    </div>
  );
}
